import React, { createContext, useContext, useRef, useState } from 'react';
import GSApi from 'api/Api';
import { useDataFetcher } from 'hooks/useDataFetcher/useDataFetcher';
import { WatchlistType, WatchlistTypeValue, WatchlistResponse } from 'types/WatchlistTypes';
import { getWatchlistTypes } from 'utils/fnWatchlist';
import { useGlobalNetworkError, useGlobalNoInternet } from 'hooks/withNetworkCheck/withNetworkCheck';

const useWatchlistService = () => {
    const { onNoInternet } = useGlobalNoInternet();
    const { onNetworkError } = useGlobalNetworkError();

    const { fetcher: watchlistFetcher } = useDataFetcher<WatchlistResponse, any>(
        (params) =>
            GSApi.getWatchlist(params?.page, params?.per_page, params?.image_size, params?.watchlist_type, params?.image_aspect_ratio),
        onNoInternet,
        onNetworkError
    );

    const [watchlists, setWatchlists] = useState<Record<WatchlistTypeValue, WatchlistResponse | null>>({
        [WatchlistType.MOVIES]: null,
        [WatchlistType.SERIES]: null,
        [WatchlistType.ALL]: null,
    });

    const watchlistFetchingRef = useRef(false);

    const getWatchlistByType = (type: WatchlistTypeValue = WatchlistType.ALL, page = '1', per_page = 10, image_size = 'medium') => {
        const image_aspect_ratio = type === WatchlistType.MOVIES ? '2:3' : '16:9';

        return watchlistFetcher({
            page,
            per_page,
            image_size,
            watchlist_type: type as string,
            image_aspect_ratio,
        }).then((response) => {
            const data = response?.response;

            if (data) {
                return {
                    results: data.results,
                    count: data.count,
                    pages: data.pages,
                    image_aspect_ratio,
                    watchlist_type: type,
                };
            }
            return null;
        });
    };

    const getWatchlist = async () => {
        if (watchlistFetchingRef.current) {
            return [];
        }
        watchlistFetchingRef.current = true;

        const types: WatchlistTypeValue[] = [WatchlistType.MOVIES, WatchlistType.SERIES];
        const results = await Promise.all(types.map((type) => getWatchlistByType(type).catch(() => null)));

        watchlistFetchingRef.current = false;

        setWatchlists((prev) => {
            const newWatchlists = { ...prev };
            results.forEach((response) => {
                if (response?.watchlist_type) {
                    newWatchlists[response.watchlist_type] = response;
                }
            });
            return newWatchlists;
        });

        return results.filter(Boolean);
    };

    return {
        watchlists,
        getWatchlist,
        getWatchlistByType,
        getWatchlistTypes,
    };
};

export const WatchlistContext = createContext<ReturnType<typeof useWatchlistService>>(null);

export const WatchlistProvider = ({ children }) => {
    const context = useWatchlistService();

    return <WatchlistContext.Provider value={context}>{children}</WatchlistContext.Provider>;
};

export const useWatchlist = () => useContext(WatchlistContext);
